import React, { useEffect, useState } from "react";
import "../../assets/styles/card.css";
import "../../assets/styles/toggle.css";
import BASE_URL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function SystemPreferences({ customerId = null }) {
  const isAdminMode = Boolean(customerId);
  const [preferredMode, setPreferredMode] = useState(null);
  const [faceRecognitionMode, setFaceRecognitionMode] = useState(null);
  const [logsMode, setLogsMode] = useState(null);
  const [debugMode, setDebugMode] = useState(null);
  const [nightVision, setNightVision] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [feedback, setFeedback] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(false);
  const [navigationSummary, setNavigationSummary] = useState(true);
  const [gesture, setGesture] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPreference = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        const url = isAdminMode
          ? `${BASE_URL}/api/preferences/get-customer-preferences/?cust_id=${customerId}`
          : `${BASE_URL}/api/preferences/get-my-preferred-mode/`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPreferredMode(data.preferred_mode);
          setFaceRecognitionMode(data.face_recognition_mode);
          setLogsMode(data.logs_mode);
          setDebugMode(data.debug_mode);
          setNightVision(data.night_vision || false);
          setPlaybackSpeed(data.playback_speed ?? 1.0);
          setFeedback(data.feedback || false);
          setWelcomeMessage(data.welcome_message || false);
          setNavigationSummary(data.navigation_summary ?? true);
          setGesture(data.gesture ?? true);
        } else {
          console.error("Failed to fetch preference");
        }
      } catch (error) {
        console.error("Error fetching preference:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPreference();
  }, [customerId, isAdminMode]);

  const patchPreference = async (key, value, setter) => {
    try {
      const token = localStorage.getItem("jwt_token");
      const url = isAdminMode
        ? `${BASE_URL}/api/preferences/admin/update-preference-mode/`
        : `${BASE_URL}/api/preferences/update-preference-mode/`;
      const body = isAdminMode
        ? { [key]: value, cust_id: customerId }
        : { [key]: value };
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setter(value);
        toast.success(`${key.replace(/_/g, " ")} updated!`);
      } else {
        toast.error(`Failed to update ${key}`);
      }
    } catch (error) {
      console.error(`Error updating ${key}:`, error);
      toast.error(`Error updating ${key}`);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <div className="pref-card-container">
        <h2 className="pref-card-title">System Preferences</h2>
        <p className="pref-card-description">
          {isAdminMode
            ? "Configure this customer's interaction preferences and system settings."
            : "Configure your interaction preferences and system settings."}
        </p>
        {loading ? (
          <p className="pref-card-loading">Loading...</p>
        ) : (
          <>
            <div className="pref-row">
              <span className="pref-card-mode">
                Preferred Mode: &nbsp;{" "}
                <strong>{preferredMode === 0 ? "Text" : "Voice"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle preferred mode"
                  checked={preferredMode === 1}
                  onChange={() =>
                    patchPreference(
                      "preferred_mode",
                      preferredMode === 0 ? 1 : 0,
                      setPreferredMode
                    )
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Face Recognition: &nbsp;{" "}
                <strong>
                  {faceRecognitionMode === 1 ? "Enabled" : "Disabled"}
                </strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle face recognition"
                  checked={faceRecognitionMode === 1}
                  onChange={() =>
                    patchPreference(
                      "face_recognition_mode",
                      faceRecognitionMode === 0 ? 1 : 0,
                      setFaceRecognitionMode
                    )
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Night Vision: &nbsp;{" "}
                <strong>{nightVision ? "Enabled" : "Disabled"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle night vision"
                  checked={nightVision === true}
                  onChange={() =>
                    patchPreference("night_vision", !nightVision, setNightVision)
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Playback Speed: &nbsp;{" "}
                <strong>{playbackSpeed}x</strong>
              </span>
              <select
                className="pref-select"
                aria-label="Playback speed"
                value={playbackSpeed}
                onChange={(e) =>
                  patchPreference("playback_speed", parseFloat(e.target.value), setPlaybackSpeed)
                }
              >
                {[0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0].map((speed) => (
                  <option key={speed} value={speed}>{speed}x</option>
                ))}
              </select>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Voice Feedback: &nbsp;{" "}
                <strong>{feedback ? "Enabled" : "Disabled"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle voice feedback"
                  checked={feedback === true}
                  onChange={() =>
                    patchPreference("feedback", !feedback, setFeedback)
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Welcome Message: &nbsp;{" "}
                <strong>{welcomeMessage ? "Enabled" : "Disabled"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle welcome message"
                  checked={welcomeMessage === true}
                  onChange={() =>
                    patchPreference("welcome_message", !welcomeMessage, setWelcomeMessage)
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Navigation Summary: &nbsp;{" "}
                <strong>{navigationSummary ? "Enabled" : "Disabled"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle navigation summary"
                  checked={navigationSummary === true}
                  onChange={() =>
                    patchPreference(
                      "navigation_summary",
                      !navigationSummary,
                      setNavigationSummary
                    )
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Gesture: &nbsp;{" "}
                <strong>{gesture ? "Enabled" : "Disabled"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle gesture"
                  checked={gesture === true}
                  onChange={() =>
                    patchPreference("gesture", !gesture, setGesture)
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Keeping Logs: &nbsp;{" "}
                <strong>{logsMode === 1 ? "Enabled" : "Disabled"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle keeping logs"
                  checked={logsMode === 1}
                  onChange={() =>
                    patchPreference("logs_mode", logsMode === 0 ? 1 : 0, setLogsMode)
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Debugging Mode: &nbsp;{" "}
                <strong>{debugMode === 1 ? "Enabled" : "Disabled"}</strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  aria-label="Toggle debugging mode"
                  checked={debugMode === 1}
                  onChange={() =>
                    patchPreference("debug_mode", debugMode === 0 ? 1 : 0, setDebugMode)
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default SystemPreferences;
